import { ReactNode, SyntheticEvent, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Sheet,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";

interface SectionCardProps {
  title?: string;
  subtitle?: string;
  disabled?: boolean;
  actions?: ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children: ReactNode;
}

const SectionCard = ({
  title,
  subtitle,
  disabled,
  actions,
  collapsible = false,
  defaultExpanded = false,
  expanded: expandedProp,
  onExpandedChange,
  children,
}: SectionCardProps) => {
  const isControlled = expandedProp !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const expanded = isControlled ? expandedProp : internalExpanded;

  const handleAccordionChange = (_event: SyntheticEvent | null, isExpanded: boolean) => {
    if (!isControlled) setInternalExpanded(isExpanded);
    onExpandedChange?.(isExpanded);
  };

  const headerContent = (
    <Stack direction="column" rowGap={1} sx={{ flex: 1 }}>
      {title && (
        <Typography level="title-md" style={{ fontWeight: 700 }}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography level="body-sm" textColor="neutral.500">
          {subtitle}
        </Typography>
      )}
    </Stack>
  );

  const actionsNode = actions ? <div onClick={(e) => e.stopPropagation()}>{actions}</div> : null;

  const headerRow = (title || actions) && (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" columnGap={2} sx={{ width: "100%" }}>
      {headerContent}
      {actionsNode}
    </Stack>
  );

  return (
    <Sheet
      variant="outlined"
      borderRadius="lg"
      padding={6}
      sx={{
        opacity: disabled ? 0.55 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {collapsible ? (
        <AccordionGroup disableDivider transition="0.3s ease-in-out">
          <Accordion
            expanded={expanded}
            onChange={handleAccordionChange}
            disabled={disabled}
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              sx={{
                "& .MuiAccordionSummary-indicator": {
                  transition: "transform 0.3s ease-in-out",
                },
              }}
            >
              {headerRow}
            </AccordionSummary>
            <AccordionDetails>{children}</AccordionDetails>
          </Accordion>
        </AccordionGroup>
      ) : (
        <Stack direction="column" rowGap={4}>
          {headerRow}
          {children}
        </Stack>
      )}
    </Sheet>
  );
};

export default SectionCard;
